import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { observer } from "mobx-react";
import AddEditMockups from "../../../pages/orders/AddEditMockups";
import MockupGeneratorModal from "../../swagstore/mockups/MockupGeneratorModal";
import CustomTypography from "../../common/form/CustomTypography";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../utils/NotificationsContext";
import type { OrderMockup } from "../../../../models/Order/OrderMockup";
import type { MockupComment } from "../../../../models/Order/MockupComment";
import type { Swagpack } from "../../../../models/Swagstore/Packs";

export type OrderMockupModalTab = "view" | "generate" | "upload";

/** Fixed height for all tabs — sized for full generate canvas (600×450) + chrome. */
const UNIFIED_MODAL_HEIGHT = "min(836px, 92vh)";

interface OrderUnifiedMockupModalProps {
  open: boolean;
  onClose: () => void;
  activeTab: OrderMockupModalTab;
  onTabChange: (tab: OrderMockupModalTab, mockupId?: number) => void;
  mockupId?: number | null;
  productId: number;
  productName?: string;
  mockups: OrderMockup[];
  comments: MockupComment[];
  canDeleteMockup?: boolean;
  isCommentsFetching?: boolean;
  isMainMockupImageLoading?: boolean;
  archived?: number;
  updateMockupComment?: (mockupComment: MockupComment) => void;
  fetchMockupComment?: (mockupId: number) => void;
  deleteMockup?: (mockupId: number) => void;
  selectedMockupId?: number;
  onSelectPsdId: (id: number | null) => void;
  onGenerateMockup: (payload: {
    psd_template_id: number;
    logoFile?: File | null;
    logoUrl?: string | null;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }) => void | Promise<void>;
  onUploadLogoToLibrary: (file: File) => void;
  onDeleteUploadedLogo: (logoId: number) => Promise<void>;
  onManualUploadMockup?: (payload: { id: number; file: File }) => Promise<void>;
  onUploadMockupFiles?: (files: File[]) => Promise<void>;
}

function EditMethodCard({
  title,
  description,
  icon,
  accent,
  onClick,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
  onClick: () => void;
}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        flex: 1,
        p: 2.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        transition: "all 0.2s ease",
        background: `linear-gradient(135deg, ${accent}08 0%, #ffffff 100%)`,
        "&:hover": {
          borderColor: accent,
          boxShadow: `0 8px 24px ${accent}22`,
        },
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${accent}14`,
          color: accent,
          mb: 1.5,
        }}
      >
        {icon}
      </Box>
      <CustomTypography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
        {title}
      </CustomTypography>
      <CustomTypography variant="caption" color="text.secondary">
        {description}
      </CustomTypography>
    </Paper>
  );
}

const OrderUnifiedMockupModal: React.FC<OrderUnifiedMockupModalProps> = observer(
  ({
    open,
    onClose,
    activeTab,
    onTabChange,
    mockupId,
    productId,
    productName,
    mockups,
    comments,
    canDeleteMockup,
    isCommentsFetching,
    isMainMockupImageLoading,
    archived = 0,
    updateMockupComment,
    fetchMockupComment,
    deleteMockup,
    selectedMockupId,
    onSelectPsdId,
    onGenerateMockup,
    onUploadLogoToLibrary,
    onDeleteUploadedLogo,
    onManualUploadMockup,
    onUploadMockupFiles,
  }) => {
    const { mockupConfigStore, projectSourcingOpsStore } = useStores();
    const { showError } = useNotification();
    const [editChoiceMockupId, setEditChoiceMockupId] = useState<number | null>(null);

    useEffect(() => {
      if (!open) setEditChoiceMockupId(null);
    }, [open]);

    const product = useMemo<Swagpack | null>(
      () =>
        productId
          ? ({
              id: productId,
              label: productName || `Product ${productId}`,
            } as Swagpack)
          : null,
      [productId, productName],
    );

    const isEditMode = Boolean(mockupId);
    const isBusy =
      projectSourcingOpsStore.generatingOrderMockup ||
      Boolean(
        isEditMode &&
          mockupId &&
          projectSourcingOpsStore.updatingOrderMockupId === mockupId,
      );

    const handleTabSwitch = (tab: OrderMockupModalTab) => {
      if (tab === "view") {
        onTabChange("view");
        return;
      }
      if (isEditMode && (tab === "generate" || tab === "upload")) {
        onTabChange(tab, mockupId ?? undefined);
        return;
      }
      onTabChange(tab);
    };

    const handleEditMockupRequest = (id: number) => {
      setEditChoiceMockupId(id);
    };

    const handleEditMethodSelect = (tab: "generate" | "upload") => {
      if (!editChoiceMockupId) return;
      onTabChange(tab, editChoiceMockupId);
      setEditChoiceMockupId(null);
    };

    return (
      <>
        <Dialog
          open={open}
          onClose={() => {
            if (isBusy) return;
            onClose();
          }}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              position: "relative",
              width: "100%",
              maxWidth: 1120,
              height: UNIFIED_MODAL_HEIGHT,
              minHeight: UNIFIED_MODAL_HEIGHT,
              maxHeight: UNIFIED_MODAL_HEIGHT,
              display: "flex",
              flexDirection: "column",
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 24px 48px rgba(15, 23, 42, 0.16)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background:
                  "linear-gradient(90deg, #4F46E5 0%, #7C3AED 50%, #EC4899 100%)",
                zIndex: 1,
              },
            }}
          >
            <DialogTitle
              sx={{
                pr: 6,
                pt: 2.5,
                pb: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <CustomTypography variant="heading5" fontWeight={700}>
                {productName || "Product Mockups"}
              </CustomTypography>
              <CustomTypography variant="caption" color="text.secondary">
                View, generate, or upload mockups in one place
              </CustomTypography>
              <IconButton
                aria-label="Close"
                onClick={onClose}
                disabled={isBusy}
                sx={{
                  position: "absolute",
                  right: 12,
                  top: 12,
                  bgcolor: "grey.50",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <Box sx={{ px: 3, pb: 0.5 }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => handleTabSwitch(value as OrderMockupModalTab)}
                sx={{
                  minHeight: 44,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    minHeight: 44,
                    color: "text.secondary",
                  },
                  "& .Mui-selected": { color: "primary.main" },
                  "& .MuiTabs-indicator": {
                    height: 3,
                    borderRadius: "3px 3px 0 0",
                  },
                }}
              >
                <Tab value="view" label="View mockups" />
                <Tab value="generate" label="Generate" />
                <Tab value="upload" label="Upload" />
              </Tabs>
            </Box>
          </Box>

          {isEditMode && activeTab !== "view" && (
            <Box
              sx={{
                px: 3,
                py: 1.25,
                bgcolor: "rgba(79, 70, 229, 0.06)",
                borderBottom: "1px solid",
                borderColor: "rgba(79, 70, 229, 0.12)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                size="small"
                aria-label="Back to mockups"
                onClick={() => onTabChange("view")}
                sx={{ bgcolor: "background.paper" }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <CustomTypography variant="body2" fontWeight={600}>
                Editing mockup #{mockupId}
              </CustomTypography>
              <CustomTypography variant="caption" color="text.secondary">
                — {activeTab === "generate" ? "regenerate with logo" : "replace image"}
              </CustomTypography>
            </Box>
          )}

          <DialogContent
            dividers
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              p: 2,
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              bgcolor: "grey.50",
            }}
          >
            {isBusy && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: (theme) => theme.zIndex.modal + 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "all",
                }}
              >
                <CircularProgress size={48} />
              </Box>
            )}
            {activeTab === "view" && (
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  p: 2.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <AddEditMockups
                  embedded
                  archived={archived}
                  canDeleteMockup={canDeleteMockup}
                  product={{ id: productId, name: productName || "" }}
                  mockups={mockups}
                  comments={comments}
                  updateMockupComment={updateMockupComment}
                  fetchMockupComment={fetchMockupComment}
                  isCommentsFetching={isCommentsFetching}
                  deleteMockup={deleteMockup}
                  isMainMockupImageLoading={isMainMockupImageLoading}
                  selectedMockupId={selectedMockupId}
                  onNavigateToTab={(tab, mockupId) => onTabChange(tab, mockupId)}
                  onEditMockup={handleEditMockupRequest}
                />
              </Paper>
            )}

            {(activeTab === "generate" || activeTab === "upload") && (
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  py: 1,
                  px: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <MockupGeneratorModal
                  embedded
                  open
                  hideInternalTabs
                  controlledTab={activeTab}
                  product={product}
                  onClose={onClose}
                  initialTab={activeTab}
                  placementTypes={mockupConfigStore.placementTypes}
                  psdTemplates={mockupConfigStore.psdTemplates}
                  selectedPsdId={mockupConfigStore.selectedPsdId}
                  loadingPsd={mockupConfigStore.loadingPsd}
                  savedMockups={[]}
                  mockupOutputs={null}
                  mockupReferenceId={null}
                  generatingMockup={projectSourcingOpsStore.generatingOrderMockup}
                  updatingMockupId={projectSourcingOpsStore.updatingOrderMockupId}
                  deletingGeneratedMockupId={null}
                  editMode={isEditMode}
                  editMockupId={mockupId ?? null}
                  hideGeneratedMockupsTab
                  onSelectPsdId={onSelectPsdId}
                  onGenerateMockup={onGenerateMockup}
                  uploadedLogos={projectSourcingOpsStore.mockupGeneratorLogos}
                  loadingUploadedLogos={projectSourcingOpsStore.loadingMockupGeneratorLogos}
                  onUploadLogoToLibrary={onUploadLogoToLibrary}
                  onDeleteUploadedLogo={onDeleteUploadedLogo}
                  onDeleteMockup={async () => {}}
                  onManualUploadMockup={onManualUploadMockup}
                  onUploadMockupFiles={onUploadMockupFiles}
                  onError={(title, message) => showError(title, message || "")}
                />
              </Paper>
            )}
          </DialogContent>
        </Dialog>

        <Dialog
          open={editChoiceMockupId != null}
          onClose={() => setEditChoiceMockupId(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 0.5,
              overflow: "visible",
            },
          }}
        >
          <DialogTitle sx={{ pb: 1, pt: 2.5, pr: 6, position: "relative" }}>
            <CustomTypography variant="heading6" fontWeight={700}>
              How do you want to update this mockup?
            </CustomTypography>
            <CustomTypography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              Choose generate to place a logo on a template, or upload to replace with your own image.
            </CustomTypography>
            <IconButton
              aria-label="Close"
              onClick={() => setEditChoiceMockupId(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                bgcolor: "grey.50",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 1, pb: 3, overflow: "visible" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ py: 0.5 }}
            >
              <EditMethodCard
                title="Generate"
                description="Place a logo on a PSD template and create a new mockup."
                icon={<AutoAwesomeOutlinedIcon />}
                accent="#4F46E5"
                onClick={() => handleEditMethodSelect("generate")}
              />
              <EditMethodCard
                title="Upload"
                description="Replace this mockup with an image from your device."
                icon={<CloudUploadOutlinedIcon />}
                accent="#7C3AED"
                onClick={() => handleEditMethodSelect("upload")}
              />
            </Stack>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

export default OrderUnifiedMockupModal;
